import { useState, useEffect, useCallback } from 'react';
import useTab from 'src/containers/Product/useTab';
import Icon from 'src/components/Icon';
import ButtonDuo from 'src/components/ButtonDuo';
import useCustomHistory from 'src/hooks/useCustomHistory';
import { PRODUCT_CATEGORY, PRODUCT_REGION } from 'src/constants/product';
import { useUser } from 'src/UserContext';
import { getProduct, getProducts } from 'src/services/checkout';
import { userProducts, userProductsCreated } from 'src/services/api';
import { useTranslation } from 'react-i18next';
import { getCurrency, isKorean } from 'src/services/util';
import { useModal } from 'src/ModalContext';
import { useLocation } from 'react-router-dom';
import NumberFormat from 'react-number-format';
import { KY_KARAOKE_NAME, KY_BIXBY_NAME } from 'src/constants/ky';

export default function Purchase(): JSX.Element {
  const history = useCustomHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { isInit, isLogin, userInfo } = useUser();
  const { tab, setTab, TabComponent } = useTab();
  const [productList, setProductList] = useState<CheckoutProductData[]>([]);
  const [userProductList, setUserProductList] = useState<CheckoutUserProduct[]>([]);
  const [viewList, setViewList] = useState<Record<string, CheckoutProductData[]>>({});
  const { showModal, errorModal } = useModal();

  useEffect(() => {
    if (!isInit) return;

    if (isLogin) {
      userProducts({
        searchStartDate: '1970-01-01T00:00:00.000Z',
        category: tab,
      }).then((res) => {
        setUserProductList(res.products);
      });
    }

    getProducts()
      .then((res) => {
        setProductList(res);
      })
      .catch(errorModal);
  }, [isInit, isLogin]);

  useEffect(() => {
    if (productList.length > 0) {
      const result = productList.reduce<Record<string, CheckoutProductData[]>>((acc, product) => {
        if (!acc[product.category]) {
          acc[product.category] = [];
        }
        if (userInfo.country) {
          if (userInfo.country === PRODUCT_REGION.KR) {
            product.region === userInfo.country && acc[product.category].push(product);
          } else {
            setTab(PRODUCT_CATEGORY.KARAOKE);
            product.region === PRODUCT_REGION.GLOBAL && acc[product.category].push(product);
          }
        } else {
          if (isKorean()) {
            product.region === PRODUCT_REGION.KR && acc[product.category].push(product);
          } else {
            setTab(PRODUCT_CATEGORY.KARAOKE);
            product.region === PRODUCT_REGION.GLOBAL && acc[product.category].push(product);
          }
        }
        return acc;
      }, {});
      setViewList(result);
    }
  }, [productList, i18n.languages[0]]);

  const getUserProduct = useCallback(
    (id) => {
      return userProductList.find(({ productId }) => id === productId);
    },
    [userProductList],
  );

  const goPayment = useCallback(
    async (e, obj) => {
      e.stopPropagation();
      e.preventDefault();
      if (isLogin) {
        const product = await getProduct(obj.id);
        userProductsCreated()
          .then((res) => {
            if (res.products.length > 0) {
              const activatedProduct = res.products.find((value) => value.category === product.category);

              if (activatedProduct) {
                showModal({
                  message: t('product/purchase/already_ticket_exists', {
                    product: isKorean() ? res.products[0].koName : res.products[0].enName,
                  }),
                  success: () => {
                    return;
                  },
                });
                return;
              }
            }
            history.push(`/payment`, {
              productId: obj.id,
              afterPaymentEndpoint: location.pathname,
            });
          })
          .catch(errorModal);
      } else {
        showModal({
          message: t('drawer/login_error'),
          success: () => {
            history.push('/login');
          },
          fail: () => {
            return;
          },
        });
      }
    },
    [isLogin, history],
  );
  return (
    <>
      <div className="container mt-6">
        <div className="flex justify-between h-7">
          <div className="N_20_R text-black">{t('product/purchase/title')}</div>
        </div>
      </div>
      <div className="container">
        <div className="mt-4 -mx-6">
          {((userInfo.country && userInfo.country === PRODUCT_REGION.KR) || (!userInfo.country && isKorean())) && TabComponent}
        </div>
      </div>
      <div className="bg-light-gray-1">
        <div className="container pt-6 pb-10">
          <div className="flex justify-between items-center">
            <div>
              <div className="N_16_M text-black">{tab === PRODUCT_CATEGORY.KARAOKE ? KY_KARAOKE_NAME : KY_BIXBY_NAME}</div>
              <div className="N_14_R text-greyish-brown flex items-center mt-1">
                {tab === PRODUCT_CATEGORY.KARAOKE ? t('product/purchase/sub_title_karaoke') : t('product/purchase/sub_title_bixby')}
              </div>
            </div>
            <Icon name={tab === PRODUCT_CATEGORY.KARAOKE ? 'tv' : 'bixby'} size="48" />
          </div>
          <div className="mt-6 cursor-pointer">
            {viewList &&
              viewList[tab] &&
              viewList[tab].map((obj: CheckoutProductData, index: number) => {
                return (
                  <div
                    key={index}
                    className="border border-ice-blue rounded bg-white pt-6 pb-5 px-5 mt-3"
                    onClick={() => {
                      history.push(`/product/purchase/${obj.id}`);
                    }}
                  >
                    {obj.subscription && (
                      <div className="flex mb-4">
                        <div className="N_10_R text-white bg-light-grey-blue rounded-sm px-2 h-5 flex justify-center items-center">
                          {t('payment/complete/badge_billing')}
                        </div>
                      </div>
                    )}
                    <div className="mt-0.5 flex justify-between flex-wrap">
                      <div className="N_16_R w-2/3 text-black flex flex-wrap" style={{ wordBreak: 'keep-all' }}>
                        {isKorean() ? obj.koName : obj.enName}
                      </div>
                      <div className="N_16_M text-black">
                        {obj.subscription && obj.promotionCycle ? (
                          <NumberFormat
                            value={getUserProduct(obj.id) ? obj.amount : obj.promotionAmount}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={getCurrency(obj.region)}
                          />
                        ) : (
                          <NumberFormat value={obj.amount} displayType={'text'} thousandSeparator={true} prefix={getCurrency(obj.region)} />
                        )}
                      </div>
                    </div>
                    <div className="mt-6">
                      <ButtonDuo
                        title1={t('product/purchase/button_detail')}
                        title2={t('product/purchase/button_purchase')}
                        onClick1={() => {
                          return;
                        }}
                        onClick2={(e) => goPayment(e, obj)}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </>
  );
}
